// @givmo/app-admin — CSV export plumbing for Reports → Exports (EP-WEB7).
//
// TWO kinds of CSV live on this surface, and they are built completely differently. Keeping the
// distinction explicit here is the point of this module:
//
//   SERVER-BUILT (transactions, tax, impact) — the backend renders the CSV and streams it. The client
//   only opts out of JSON parsing and saves the blob, exactly like the §321 charity exports. Those go
//   through `runCsvDownload` in `apps/charity/src/features/grants/csv-download.ts`; do NOT re-implement
//   it here.
//
//   CLIENT-BUILT (enrollment) — there is no server endpoint for the roster export, so the client pages
//   `/admin/company/employees` to exhaustion and writes the CSV itself. That is what this module is for.
//
// **Why a client build needs care.** `page[limit]` is silently CLAMPED, not rejected: asking for 100000
// returns at most `MAX_PAGINATION_LIMIT` rows with HTTP 200. And the api-client's response middleware
// strips the `{data, meta, links}` envelope down to the bare array, so the client has **no row-count
// oracle** — nothing tells it how many rows exist. A naive exporter therefore cannot distinguish "that
// was everything" from "that was the first page", and would ship a truncated file that claims to be
// complete. `collectAllPages` below is the answer: page until a SHORT page proves exhaustion.

import type { Options } from '@givmo/api-client';

/**
 * Per-request fragment opting a download call OUT of JSON parsing and into a BINARY (blob) response WITH
 * the raw `Response` exposed (`responseStyle:'fields'`), so the caller can read the Content-Disposition
 * filename via `readBinaryMeta`. Same api-client capability super-admin's `lib/response-modes.ts` uses.
 *
 * This module is the admin app's ONE home for download plumbing (it previously lived private inside
 * `documents-page.tsx`, whose comment noted the app had no shared module — this is now that module).
 * Each app keeps its own copy rather than importing across app boundaries, matching the charity app.
 */
export const binaryDownloadOptions = {
  parseAs: 'blob',
  responseStyle: 'fields',
} as const satisfies Pick<Options, 'parseAs' | 'responseStyle'>;

/**
 * Coerce a `parseAs:'blob'` response body into something {@link blobSave} can actually save.
 *
 * ⚠ `data instanceof Blob` IS NOT A RELIABLE TEST HERE, and the obvious fallback is worse than the
 * check. The fetch layer's `Response.blob()` produces a Blob from **Node's** realm; under the test
 * environment the operator compares that against jsdom's `globalThis.Blob` and answers **false** on
 * a value whose `constructor.name` is `Blob` and whose `.text()` works. The natural repair —
 * `new Blob([data as BlobPart])` — then makes it silently wrong rather than merely defensive: jsdom's
 * constructor does not recognise a foreign Blob as a `BlobPart`, so it stringifies it and the saved
 * file contains the literal text `[object Blob]`. A download that produces a 13-byte file saying
 * `[object Blob]` is the failure this exists to prevent.
 *
 * So: keep anything BLOB-LIKE as-is (duck-typed on `arrayBuffer`, which every realm's Blob has), and
 * wrap only what genuinely is not one — a string body, or nothing. Correct in a browser and under
 * test, and it does not depend on which realm produced the value.
 */
export function asSaveableBlob(data: unknown, type: string): Blob {
  if (typeof (data as { arrayBuffer?: unknown } | null)?.arrayBuffer === 'function') {
    return data as Blob;
  }
  return new Blob([(data ?? '') as BlobPart], { type });
}

/** Where a finished file goes: saved now ({@link blobSave}), or held by a caller until a click. */
export type SaveFile = (blob: Blob, filename: string) => void;

// ══ ONE SAVE PER INTERACTION ═════════════════════════════════════════════════════════════════════
//
// A browser lets a page start ONE download per user interaction. A second save with no press, click
// or key in between is held behind Chrome's "Download multiple files" prompt, or blocked outright
// once the person has blocked the site, and the page is never told: `blobSave` returns the same
// either way. Reproduced in a plain Chrome at the EP-WEB26 review: two Reports tiles clicked a moment
// apart saved only the first file while both said "Downloaded". Automation hides it: a
// Playwright-driven browser sets its own download behaviour and saves every file.
//
// So this module keeps the page's one "save turn". Every save through `blobSave` takes it: a tile's
// CSV, a Tax documents PDF, the enrollment file. Any press or key on the page gives it back. A caller
// that must never claim a file the browser held (the Reports tiles, which prepare independently)
// asks `saveTurnIsFree()` first and, when it is taken, holds the file until the person's next click
// (EP-WEB26, Jonathan's ruling 2026-09-29 on the review's F1: option (a)).

let saveTurnFree = true;
let listening = false;

function listenForInteraction(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  const giveBack = () => {
    saveTurnFree = true;
  };
  // Capture phase: the press that lands on a save button gives the turn back before its own
  // handler runs, as the browser counts it.
  window.addEventListener('pointerdown', giveBack, true);
  window.addEventListener('keydown', giveBack, true);
}

/**
 * Whether a save now would reach the person's downloads without the browser holding it: nothing has
 * been saved on this page since their last press, click or key. The page starts with the turn free.
 */
export function saveTurnIsFree(): boolean {
  listenForInteraction();
  return saveTurnFree;
}

/** Trigger an in-browser download of `blob` as `filename` via a transient object-URL anchor. */
export function blobSave(blob: Blob, filename: string): void {
  listenForInteraction();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
  saveTurnFree = false;
}

/** Fields that must be quoted per RFC 4180, plus the leading characters spreadsheets treat as formulas. */
const CSV_NEEDS_QUOTING = /[",\r\n]/;
const CSV_FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r'];
/** A clean numeric literal — a server-computed money/count value that must stay numeric. */
const CSV_CLEAN_NUMERIC = /^-?\d+(?:\.\d+)?$/;

/**
 * Neutralize a spreadsheet formula-injection vector in one cell, then quote per RFC 4180.
 *
 * Mirrors the backend's `csv_safe` (SEC-BE13) deliberately: a client-built CSV is opened in the same
 * spreadsheet apps as a server-built one, so it needs the same protection, and having the two rules
 * agree means a reader cannot tell which pipeline produced a file. A clean numeric literal is exempt —
 * prefixing a negative number would corrupt SUM() for zero added safety.
 */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (raw.length === 0) return '';

  // `raw[0]` is `string | undefined` under noUncheckedIndexedAccess; the length guard above already
  // rules out undefined, but read it into a narrowed local rather than asserting.
  const first = raw.charAt(0);
  const neutralized =
    CSV_FORMULA_PREFIXES.includes(first) && !CSV_CLEAN_NUMERIC.test(raw) ? `'${raw}` : raw;

  return CSV_NEEDS_QUOTING.test(neutralized) ? `"${neutralized.replace(/"/g, '""')}"` : neutralized;
}

/**
 * Build CSV text from a column set and rows. `columns` fixes both the header and the per-row order, so
 * a row object with extra keys cannot shift a column, and a row missing a key yields an empty cell
 * rather than a shifted one.
 *
 * Uses `\r\n` (RFC 4180) — Excel on Windows is the dominant consumer of a finance export.
 */
export function buildCsv<T extends string>(
  columns: readonly T[],
  rows: readonly Partial<Record<T, unknown>>[],
): string {
  const header = columns.map(csvCell).join(',');
  const body = rows.map((row) => columns.map((column) => csvCell(row[column])).join(','));
  return [header, ...body].join('\r\n');
}

/**
 * A UTF-8 BOM. Built with `fromCharCode` rather than written literally or as a `\uFEFF` escape so this
 * source file stays pure ASCII — a raw BOM mid-file trips the `no-irregular-whitespace` lint.
 */
const UTF8_BOM = String.fromCharCode(0xfeff);

/** Save CSV `text` as `filename` via the shared object-URL anchor helper (or the caller's `save`). */
export function downloadCsv(text: string, filename: string, save: SaveFile = blobSave): void {
  // The BOM is what makes Excel read the file as UTF-8; without it an accented charity name opens as
  // mojibake. This file is meant to be opened, not merely parsed.
  save(new Blob([UTF8_BOM + text], { type: 'text/csv;charset=utf-8' }), filename);
}

/** Stamp a client-built export filename, matching the server exports' `<name>.csv` shape. */
export function stampedCsvName(kind: string): string {
  return `givmo_${kind}_${new Date().toISOString().slice(0, 10)}.csv`;
}

/** How many rows a single page requests. Well under the server clamp, so it is never silently reduced. */
export const EXPORT_PAGE_LIMIT = 100;

/**
 * A hard ceiling on pages fetched, so a server that keeps returning full pages (a bug, or a roster that
 * grows mid-export) cannot spin forever. At {@link EXPORT_PAGE_LIMIT} this covers 20k rows.
 *
 * Reaching it is treated as an ERROR by {@link collectAllPages}, never as a successful short export —
 * an exporter that silently stopped early is the exact failure this whole module exists to prevent.
 */
export const EXPORT_MAX_PAGES = 200;

export class ExportTooLargeError extends Error {
  constructor(rowCount: number) {
    super(
      `Export exceeded ${rowCount} rows without reaching the end. ` +
        'Narrow the export or use a server-side export.',
    );
    this.name = 'ExportTooLargeError';
  }
}

/**
 * Page `fetchPage` to exhaustion and return every row, in page order.
 *
 * **Exhaustion is proven by a SHORT page** (`rows.length < limit`), which is the only signal available:
 * there is no `meta.total` on the unwrapped body, and an empty final page is not guaranteed (a roster
 * whose size is an exact multiple of the limit ends on a full page, so the loop then fetches one more
 * and gets zero rows — also short, also terminal). A full page always means "keep going".
 *
 * Throws {@link ExportTooLargeError} rather than returning a partial set if the page ceiling is hit.
 */
export async function collectAllPages<T>(
  fetchPage: (offset: number, limit: number) => Promise<T[]>,
  limit: number = EXPORT_PAGE_LIMIT,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; page < EXPORT_MAX_PAGES; page += 1) {
    const rows = await fetchPage(all.length, limit);
    all.push(...rows);
    if (rows.length < limit) return all;
  }
  throw new ExportTooLargeError(all.length);
}
